import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, Car, ShieldCheck, Clock, MapPin, Users, Award, Handshake, TrendingUp, Star, Phone, CheckCircle2 } from 'lucide-react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import api from '../lib/api';
import SEOHead, { faqSchema, webPageSchema } from '../components/SEOHead';
import BlogCard from '../components/BlogCard';
import FAQSection from '../components/FAQSection';
import CTABlock from '../components/CTABlock';
import NewsletterBlock from '../components/NewsletterBlock';
import AnimateIn, { StaggerChildren, StaggerItem } from '../components/AnimateIn';

const HERO_BG = "/hero-desktop.webp";
const HERO_BG_MOBILE = "/hero-mobile.webp";

const serviceCards = [
  { icon: Car, title: 'Vendre votre voiture', desc: 'Vendez 2x plus vite et gagnez 20% de plus qu\'une reprise', href: '/vendre-voiture-montelimar', accent: true },
  { icon: Calculator, title: 'Estimer votre véhicule', desc: 'Estimation gratuite et sans engagement par un conseiller', href: '/estimer-voiture' },
  { icon: ShieldCheck, title: 'Acheter une voiture', desc: 'Véhicules préparés, garantie moteur-boîte-pont et paiement sécurisé', href: '/acheter-voiture-montelimar' },
  { icon: Handshake, title: 'Dépôt-vente', desc: 'On gère la vente pour vous, de A à Z', href: '/depot-vente-montelimar' },
];

const stats = [
  { value: 16, suffix: '', label: "ans d'expérience" },
  { value: 35000, suffix: '+', label: 'véhicules vendus' },
  { value: 40, suffix: '+', label: 'agences en France' },
  { value: 4.8, suffix: '/5', label: 'avis Google', isDecimal: true },
];

const whyUs = [
  { icon: Award, title: '16 ans d\'expérience', desc: 'Franchise du réseau jevendsvotreauto.com, leader national de la vente auto entre particuliers.' },
  { icon: ShieldCheck, title: 'Paiement 100% sécurisé', desc: 'Transactions via un compte séquestre. Zéro risque pour le vendeur comme pour l\'acheteur.' },
  { icon: Clock, title: '3 semaines en moyenne', desc: 'Vendez votre véhicule 2x plus vite qu\'entre particuliers grâce à notre réseau.' },
  { icon: Users, title: '4,8/5 sur Google', desc: 'Note validée par nos clients sur Google My Business. 13 avis vérifiés.' },
];

const steps = [
  { num: '01', title: 'Estimation gratuite', desc: 'Remplissez le formulaire ou appelez-nous pour une estimation immédiate et sans engagement.' },
  { num: '02', title: 'Estimation du véhicule', desc: 'Un conseiller évalue votre véhicule dans notre agence à Montélimar. Estimation juste et précise.' },
  { num: '03', title: 'Mise en vente', desc: 'Votre véhicule est mis en vente sur nos plateformes. Nous gérons les visites et les négociations.' },
  { num: '04', title: 'Vente finalisée', desc: 'Signez et recevez votre paiement sécurisé via compte séquestre. Nous gérons l\'administratif.' },
];

const testimonials = [
  { name: 'Franka Di Dio', text: 'Superbe expérience ! L\'accueil est chaleureux, l\'équipe est professionnelle et à l\'écoute. Vente effectué rapidement et avec sérieux. On sent qu\'on peut leur faire confiance, ce qui est très rassurant. Je recommande ce garage les yeux fermés et j\'y retournerai sans hésiter !', rating: 5 },
  { name: 'Raphaël', text: 'Je viens d\'acheter un véhicule chez eux et tout s\'est très bien passé, une équipe jeune et très professionnelle. Ils ont répondu à toutes mes questions, joignables très rapidement, même après la livraison du véhicule ils restent entièrement disponibles. Je recommande à 100 %.', rating: 5 },
  { name: 'AdiTM', text: 'Excellente expérience du début à la fin. Personnel accueillant, très professionnel et à l\'écoute. L\'estimation de mon véhicule a été rapide et transparente, avec une prise en charge efficace. Transaction simple et sécurisée. Je recommande à 100 %.', rating: 5 },
  { name: 'Gisèle Champion', text: 'Très professionnel et de très bon conseils, en 15 jours ma voiture a été vendue et tout a été réglé dans le temps comme prévu par le commercial. Je recommande vraiment, vous pouvez y aller rassuré, une très belle équipe et vous avez à faire à des pro.', rating: 5 },
];

function AnimatedCounter({ value, suffix = '', isDecimal = false }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const duration = 2000;
    const start = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(isDecimal ? parseFloat((eased * value).toFixed(1)) : Math.floor(eased * value));
      if (progress === 1) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, value, isDecimal]);

  return <span ref={ref}>{isDecimal ? count.toFixed(1) : count.toLocaleString('fr-FR')}{suffix}</span>;
}

export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [faq, setFaq] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get('/articles?limit=3').then(r => setArticles(r.data.articles || [])).catch(() => {});
    api.get('/faq').then(r => setFaq(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEOHead
        title="Vente et Estimation Auto à Montélimar | GuideAutoFrance.fr"
        description="Vendez votre voiture rapidement à Montélimar. Estimation gratuite, dépôt-vente professionnel, paiement sécurisé. 16 ans d'expérience automobile."
        schemas={[
          webPageSchema('Accueil', 'Vente, achat, estimation et dépôt-vente automobile à Montélimar', '/'),
          ...(faq.length > 0 ? [faqSchema(faq.slice(0, 6))] : [])
        ]}
      />

      {/* ══════ HERO ══════ */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-[100vh] flex items-center overflow-hidden">
        <motion.div style={{ y: heroImgY }} className="absolute inset-0">
          <img
            src={HERO_BG}
            srcSet={`${HERO_BG_MOBILE} 480w, ${HERO_BG} 1280w`}
            sizes="100vw"
            alt="Voiture premium sur fond sombre - GuideAutoFrance Montélimar"
            className="w-full h-full object-cover scale-110"
            loading="eager"
            fetchPriority="high"
            width={1280}
            height={720}
          />
          <div className="hero-overlay absolute inset-0" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-transparent to-transparent opacity-60" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 w-full">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-6"
            >
              Montélimar — Drôme (26)
            </motion.span>
            <h1
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.08] tracking-tight mb-6"
            >
              Vendez votre voiture à Montélimar{' '}
              <span className="text-gradient-yellow">rapidement et au meilleur prix</span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-10 max-w-xl"
            >
              Estimation gratuite, dépôt-vente professionnel et rachat immédiat. Un accompagnement humain, transparent et sécurisé par des professionnels depuis plus de 16 ans.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/estimer-voiture" data-testid="hero-cta-estimate"
                className="btn-primary py-4 px-8 text-sm text-center neon-glow-hover">
                Estimer mon véhicule
              </Link>
              <Link to="/vendre-voiture-montelimar" data-testid="hero-cta-sell"
                className="btn-glass text-white font-semibold py-4 px-8 text-sm text-center">
                Vendre ma voiture
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Hero bottom stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="absolute bottom-0 left-0 right-0 z-20"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="glass rounded-t-2xl rounded-b-none px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((s) => (
                <div key={s.label} className="text-center md:text-left">
                  <div className="text-2xl md:text-3xl font-black font-['Outfit'] text-yellow-400">
                    <AnimatedCounter value={s.value} suffix={s.suffix} isDecimal={s.isDecimal} />
                  </div>
                  <div className="text-zinc-400 text-xs mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* ══════ SERVICES ══════ */}
      <section data-testid="services-section" className="py-28 md:py-36 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-3 block">Nos services</span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Comment pouvons-nous vous aider ?
            </h2>
          </AnimateIn>
          <StaggerChildren stagger={0.1} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {serviceCards.map((s, idx) => (
              <StaggerItem key={s.title}>
                <Link to={s.href} data-testid={`service-card-${idx}`}
                  className={`relative overflow-hidden glass-card rounded-2xl p-8 group card-lift block ${s.accent ? 'border-yellow-400/15 md:col-span-2' : ''}`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="flex items-start gap-5 relative z-10">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${s.accent ? 'bg-yellow-400 text-black group-hover:shadow-[0_0_30px_rgba(214,255,0,0.3)]' : 'bg-white/[0.04] text-yellow-400 group-hover:bg-yellow-400/10'}`}>
                      <s.icon className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-2 group-hover:text-yellow-400 transition-colors duration-300">{s.title}</h3>
                      <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 mt-1" />
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════ WHY US ══════ */}
      <section data-testid="why-us-section" className="py-28 md:py-36 section-dark grain-overlay relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <AnimateIn className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-3 block">Pourquoi nous</span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight">
              La confiance à Montélimar
            </h2>
          </AnimateIn>
          <StaggerChildren stagger={0.08} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyUs.map((item, idx) => (
              <StaggerItem key={item.title}>
                <div data-testid={`why-us-${idx}`}
                  className="glass-card rounded-2xl p-7 card-lift h-full">
                  <div className="w-12 h-12 bg-yellow-400/[0.08] rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-6 h-6 text-yellow-400" />
                  </div>
                  <h3 className="font-['Outfit'] font-semibold text-white mb-2 text-lg">{item.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════ HOW IT WORKS ══════ */}
      <section data-testid="how-it-works-section" className="py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-3 block">Processus</span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Comment ça marche ?
            </h2>
          </AnimateIn>
          <StaggerChildren stagger={0.12} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step, idx) => (
              <StaggerItem key={step.num}>
                <div data-testid={`step-${idx}`} className="relative h-full">
                  <div className="glass-card rounded-2xl p-7 card-lift h-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-400/[0.03] rounded-bl-full" />
                    <span className="text-5xl font-black font-['Outfit'] text-yellow-400/[0.12] block mb-3">{step.num}</span>
                    <h3 className="font-['Outfit'] font-semibold text-white mb-2 text-lg">{step.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                  {idx < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-[1px] bg-gradient-to-r from-yellow-400/30 to-transparent" />
                  )}
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════ LOCAL PRESENCE ══════ */}
      <section data-testid="local-section" className="py-28 md:py-36 section-dark grain-overlay relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimateIn direction="left">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-5 block">Ancrage local</span>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Votre agence de confiance à Montélimar
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Situés rue Raymond Louis à Montélimar dans la Drôme, nous connaissons parfaitement le marché automobile local. Notre équipe vous accueille du lundi au samedi dans un espace moderne et professionnel.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: MapPin, text: 'Rue Raymond Louis, 26200 Montélimar' },
                  { icon: Phone, text: '04 75 52 98 20' },
                  { icon: Clock, text: 'Lun-Sam : 9h-12h / 14h-18h' },
                  { icon: Star, text: 'Note 4,8/5 — 13 avis Google' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 text-zinc-300">
                    <div className="w-9 h-9 bg-yellow-400/[0.08] rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-yellow-400" />
                    </div>
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" data-testid="local-cta-contact"
                className="inline-flex items-center gap-2 text-yellow-400 font-semibold hover:gap-3 transition-all text-sm">
                Nous rendre visite <ArrowRight className="w-4 h-4" />
              </Link>
            </AnimateIn>
            <AnimateIn direction="right">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-yellow-400/[0.06] to-transparent rounded-3xl blur-2xl" />
                  <img
                  src="https://customer-assets.emergentagent.com/job_montelimar-auto/artifacts/t5kz2hxa_WhatsApp%20Image%202026-01-31%20at%2014.12.53.jpeg"
                  alt="Agence Je Vends Votre Auto Montélimar"
                  className="rounded-2xl w-full aspect-[4/3] object-cover relative z-10"
                  loading="lazy"
                  width={800}
                  height={600}
                />
                <div className="absolute -bottom-5 -left-5 z-20 glass rounded-2xl py-4 px-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-400 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">4,8/5</div>
                    <div className="text-zinc-400 text-xs">13 avis Google</div>
                  </div>
                </div>
              </div>
            </AnimateIn>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════ TESTIMONIALS ══════ */}
      <section data-testid="testimonials-section" className="py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-3 block">Témoignages</span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Ce que disent nos clients
            </h2>
            <p className="text-zinc-400 mt-4 max-w-lg mx-auto">Avis vérifiés Google — Note 4,8/5</p>
          </AnimateIn>
          <StaggerChildren stagger={0.1} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, idx) => (
              <StaggerItem key={t.name}>
                <div data-testid={`testimonial-${idx}`} className="glass-card rounded-2xl p-6 card-lift h-full flex flex-col">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed flex-1 mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-white/[0.04]">
                    <div className="w-8 h-8 bg-yellow-400/10 rounded-full flex items-center justify-center">
                      <span className="text-yellow-400 text-xs font-bold">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{t.name}</p>
                      <p className="text-zinc-400 text-xs">Avis Google</p>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      <div className="section-divider" />

      {/* ══════ LATEST ARTICLES ══════ */}
      {articles.length > 0 && (
        <section data-testid="latest-articles-section" className="py-28 md:py-36 section-dark">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-14">
              <AnimateIn>
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 mb-3 block">Blog</span>
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight">
                  Nos derniers articles
                </h2>
              </AnimateIn>
              <AnimateIn delay={0.2}>
                <Link to="/blog" className="hidden sm:inline-flex items-center gap-1.5 text-yellow-400 font-medium hover:gap-2.5 transition-all text-sm">
                  Voir tout <ArrowRight className="w-4 h-4" />
                </Link>
              </AnimateIn>
            </div>
            <StaggerChildren stagger={0.1} className="grid grid-cols-1 md:grid-cols-3 gap-7">
              {articles.map(a => (
                <StaggerItem key={a.id || a.slug}>
                  <BlogCard article={a} />
                </StaggerItem>
              ))}
            </StaggerChildren>
          </div>
        </section>
      )}

      {/* ══════ FAQ ══════ */}
      <FAQSection items={faq.slice(0, 6)} title="Questions fréquentes" subtitle="Tout ce que vous devez savoir sur nos services automobiles à Montélimar" />

      {/* ══════ NEWSLETTER ══════ */}
      <NewsletterBlock />

      {/* ══════ FINAL CTA ══════ */}
      <CTABlock
        title="Prêt à vendre votre voiture ?"
        subtitle="Obtenez une estimation gratuite en quelques minutes et vendez au meilleur prix à Montélimar."
        primaryText="Estimer mon véhicule"
        primaryLink="/estimer-voiture"
        secondaryText="Vendre ma voiture"
        secondaryLink="/vendre-voiture-montelimar"
      />
    </div>
  );
}
